import React, { useState, useEffect } from "react";
import Modal from "react-modal";
import {
  X,
  RotateCcw,
  AlertCircle,
  Loader2,
  Ticket,
  Info,
  Check,
  Building2,
  Calendar,
  ArrowRight,
  ChevronDown,
  Plus,
  Minus,
} from "lucide-react";
import { format } from "date-fns";
import { VoucherReturnModalProps } from "@/modules/eventRegistration/features/EventWaitlist/components/VoucherReturnModal/types";
import styles from "./VoucherReturnModal.module.css";

export default function VoucherReturnModal({
  isOpen,
  onClose,
  voucherRefund,
  onProcessReturn,
  isProcessing = false,
}: VoucherReturnModalProps) {
  const [vouchersToReturn, setVouchersToReturn] = useState<number>(1);
  const [returnReason, setReturnReason] = useState<string>("");
  const [notes, setNotes] = useState<string>("");
  const [notifyVoucherAdmin, setNotifyVoucherAdmin] = useState<boolean>(true);
  const [notifyEventCoordinator, setNotifyEventCoordinator] =
    useState<boolean>(true);
  const [showConfirmation, setShowConfirmation] = useState<boolean>(false);

  // Lock body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isOpen]);

  // Reset state when modal opens with new data
  useEffect(() => {
    if (voucherRefund && isOpen) {
      setVouchersToReturn(Math.min(1, voucherRefund.totalVouchers));
      setReturnReason("");
      setNotes("");
      setNotifyVoucherAdmin(true);
      setNotifyEventCoordinator(true);
      setShowConfirmation(false);
    }
  }, [voucherRefund, isOpen]);

  if (!voucherRefund) return null;

  const maxVouchers = voucherRefund.totalVouchers;

  const handleClose = () => {
    if (!isProcessing) {
      onClose();
    }
  };

  const handleProcessReturn = () => {
    if (!returnReason) return;
    setShowConfirmation(true);
  };

  const confirmReturn = () => {
    onProcessReturn(
      vouchersToReturn,
      returnReason,
      notes,
      notifyVoucherAdmin,
      notifyEventCoordinator,
    );
  };

  const handleIncrement = () => {
    if (vouchersToReturn < maxVouchers) {
      setVouchersToReturn(vouchersToReturn + 1);
    }
  };

  const handleDecrement = () => {
    if (vouchersToReturn > 1) {
      setVouchersToReturn(vouchersToReturn - 1);
    }
  };

  const returnReasons = [
    { value: "attendee_cancellation", label: "Attendee Cancellation" },
    { value: "duplicate_registration", label: "Duplicate Registration" },
    { value: "change_in_plans", label: "Change in Plans" },
    { value: "administrative_adjustment", label: "Administrative Adjustment" },
    { value: "event_cancellation", label: "Event Cancellation" },
    { value: "other", label: "Other (specify in notes)" },
  ];

  const modalStyles = {
    content: {
      position: "fixed" as const,
      top: "0",
      left: "0",
      right: "0",
      bottom: "0",
      padding: "0",
      background: "white",
      overflow: "hidden",
      border: "none",
      borderRadius: "0",
    },
    overlay: {
      backgroundColor: "rgba(0, 0, 0, 0.5)",
      zIndex: 9999,
    },
  };

  return (
    <Modal
      contentLabel="Return Event Vouchers"
      isOpen={isOpen}
      onRequestClose={handleClose}
      style={modalStyles}
    >
      <div className={styles.modalContainer}>
        {/* Fixed Header */}
        <div className={styles.modalHeader}>
          <div className={styles.headerContent}>
            <div className={styles.headerLeft}>
              <div className={styles.headerIcon}>
                <RotateCcw className={styles.headerIconSvg} />
              </div>
              <div>
                <h2 className={styles.headerTitle}>Return Event Vouchers</h2>
                <p className={styles.headerSubtitle}>
                  Process voucher returns for{" "}
                  <span className={styles.emphasis}>
                    {voucherRefund.groupName}
                  </span>
                </p>
              </div>
            </div>
            <button
              aria-label="Close modal"
              className={styles.closeButton}
              onClick={handleClose}
              type="button"
            >
              <X className={styles.closeIcon} />
            </button>
          </div>
        </div>

        {/* Info Banner */}
        {!showConfirmation && (
          <div className={styles.infoBanner}>
            <Info className={styles.infoBannerIcon} />
            <p className={styles.infoBannerText}>
              Returning vouchers will credit them back to the company's voucher
              pool. The registration remains active.
            </p>
          </div>
        )}

        {/* Scrollable Content */}
        <div className={styles.modalBody}>
          <div className={styles.modalContent}>
            {!showConfirmation ? (
              <div className={styles.formContainer}>
                {/* Return Details */}
                <div className={styles.detailsCard}>
                  <h3 className={styles.cardTitle}>
                    <Building2 className={styles.cardIcon} />
                    Return Details
                  </h3>
                  <div className={styles.detailsGrid}>
                    <div className={styles.detailItem}>
                      <span className={styles.detailLabel}>Company Name:</span>
                      <p className={styles.detailValue}>
                        {voucherRefund.groupName}
                      </p>
                    </div>
                    <div className={styles.detailItem}>
                      <span className={styles.detailLabel}>Vouchers Used:</span>
                      <p className={styles.detailValue}>
                        <Ticket className={styles.voucherIcon} />
                        {voucherRefund.totalVouchers} vouchers
                      </p>
                    </div>
                  </div>
                </div>

                {/* Voucher Return Amount */}
                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>
                    Number of Vouchers to Return
                  </label>
                  <div className={styles.numberInputContainer}>
                    <button
                      aria-label="Decrease voucher count"
                      className={styles.numberButton}
                      disabled={vouchersToReturn <= 1}
                      onClick={handleDecrement}
                      type="button"
                    >
                      <Minus className={styles.numberButtonIcon} />
                    </button>
                    <div className={styles.numberDisplay}>
                      <span className={styles.numberValue}>
                        {vouchersToReturn}
                      </span>
                      <span className={styles.numberLabel}>
                        voucher{vouchersToReturn !== 1 ? "s" : ""}
                      </span>
                    </div>
                    <button
                      aria-label="Increase voucher count"
                      className={styles.numberButton}
                      disabled={vouchersToReturn >= maxVouchers}
                      onClick={handleIncrement}
                      type="button"
                    >
                      <Plus className={styles.numberButtonIcon} />
                    </button>
                  </div>
                  <p className={styles.numberHint}>
                    Maximum: {maxVouchers} vouchers
                  </p>
                </div>

                {/* Return Reason */}
                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>
                    Reason for Return <span className={styles.required}>*</span>
                  </label>
                  <div className={styles.selectWrapper}>
                    <select
                      className={styles.select}
                      onChange={(e) => setReturnReason(e.target.value)}
                      value={returnReason}
                    >
                      <option value="">Select a reason...</option>
                      {returnReasons.map((reason) => (
                        <option key={reason.value} value={reason.value}>
                          {reason.label}
                        </option>
                      ))}
                    </select>
                    <ChevronDown className={styles.selectIcon} />
                  </div>
                </div>

                {/* Additional Notes */}
                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>
                    Notes <span className={styles.optional}>(Optional)</span>
                  </label>
                  <textarea
                    className={styles.textarea}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Add any additional context or explanation..."
                    rows={3}
                    value={notes}
                  />
                </div>

                {/* Notification Options */}
                <div className={styles.formGroup}>
                  <h4 className={styles.sectionTitle}>Notification Options</h4>
                  <div className={styles.checkboxGroup}>
                    <label className={styles.checkboxLabel}>
                      <input
                        checked={notifyVoucherAdmin}
                        className={styles.checkbox}
                        onChange={(e) =>
                          setNotifyVoucherAdmin(e.target.checked)
                        }
                        type="checkbox"
                      />
                      <div className={styles.checkboxContent}>
                        <span className={styles.checkboxTitle}>
                          Voucher Administrator
                        </span>
                        <p className={styles.checkboxDescription}>
                          Send confirmation to company voucher administrator
                        </p>
                      </div>
                    </label>
                    <label className={styles.checkboxLabel}>
                      <input
                        checked={notifyEventCoordinator}
                        className={styles.checkbox}
                        onChange={(e) =>
                          setNotifyEventCoordinator(e.target.checked)
                        }
                        type="checkbox"
                      />
                      <div className={styles.checkboxContent}>
                        <span className={styles.checkboxTitle}>
                          Event Coordinator
                        </span>
                        <p className={styles.checkboxDescription}>
                          Send notification to event coordinator
                        </p>
                      </div>
                    </label>
                  </div>
                </div>
              </div>
            ) : (
              /* Confirmation View */
              <div className={styles.confirmationContainer}>
                <div className={styles.successCard}>
                  <Check className={styles.successIcon} />
                  <div>
                    <p className={styles.successTitle}>
                      Ready to Process Return
                    </p>
                    <p className={styles.successText}>
                      You are about to return{" "}
                      <strong>{vouchersToReturn}</strong> voucher
                      {vouchersToReturn > 1 ? "s" : ""} to{" "}
                      {voucherRefund.groupName}'s pool.
                    </p>
                  </div>
                </div>

                <div className={styles.summaryCard}>
                  <h4 className={styles.summaryTitle}>Return Summary</h4>
                  <div className={styles.summaryContent}>
                    <div className={styles.summaryItem}>
                      <span className={styles.summaryLabel}>
                        Vouchers to Return:
                      </span>
                      <span className={styles.summaryValue}>
                        {vouchersToReturn}
                      </span>
                    </div>
                    <div className={styles.summaryItem}>
                      <span className={styles.summaryLabel}>
                        Return Reason:
                      </span>
                      <span className={styles.summaryValue}>
                        {
                          returnReasons.find((r) => r.value === returnReason)
                            ?.label
                        }
                      </span>
                    </div>
                  </div>
                  {notes && (
                    <div className={styles.summaryNotes}>
                      <p className={styles.summaryNotesLabel}>
                        Additional Notes:
                      </p>
                      <p className={styles.summaryNotesText}>{notes}</p>
                    </div>
                  )}
                </div>

                <div className={styles.notificationCard}>
                  <Info className={styles.notificationIcon} />
                  <div>
                    <strong className={styles.notificationTitle}>
                      Notifications will be sent to:
                    </strong>
                    <ul className={styles.notificationList}>
                      {notifyVoucherAdmin && (
                        <li>Company voucher administrator</li>
                      )}
                      {notifyEventCoordinator && <li>Event coordinator</li>}
                      {!notifyVoucherAdmin && !notifyEventCoordinator && (
                        <li>No notifications selected</li>
                      )}
                    </ul>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Fixed Footer */}
        <div className={styles.modalFooter}>
          <div className={styles.footerContent}>
            <div className={styles.footerLeft}>
              {!showConfirmation && !returnReason && (
                <p className={styles.warningMessage}>
                  <AlertCircle className={styles.warningIcon} />
                  Please select a return reason
                </p>
              )}
            </div>
            <div className={styles.footerActions}>
              {!showConfirmation ? (
                <>
                  <button
                    className={styles.cancelButton}
                    onClick={handleClose}
                    type="button"
                  >
                    Cancel
                  </button>
                  <button
                    className={`${styles.primaryButton} ${
                      !returnReason ? styles.buttonDisabled : ""
                    }`}
                    disabled={!returnReason || isProcessing}
                    onClick={handleProcessReturn}
                    type="button"
                  >
                    <RotateCcw className={styles.buttonIcon} />
                    Continue
                  </button>
                </>
              ) : (
                <>
                  <button
                    className={styles.backButton}
                    disabled={isProcessing}
                    onClick={() => setShowConfirmation(false)}
                    type="button"
                  >
                    Back
                  </button>
                  <button
                    className={`${styles.confirmButton} ${
                      isProcessing ? styles.buttonProcessing : ""
                    }`}
                    disabled={isProcessing}
                    onClick={confirmReturn}
                    type="button"
                  >
                    {isProcessing ? (
                      <>
                        <Loader2 className={styles.spinningIcon} />
                        Processing...
                      </>
                    ) : (
                      <>
                        <Check className={styles.buttonIcon} />
                        Confirm Return
                      </>
                    )}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
}
